/**
 * RCS I/O abstraction layer.
 * Provides a consistent interface for reading RCS files via byte-range
 * requests in both Node.js (fs) and browser (XMLHttpRequest) environments.
 */

import {
  HTTP2_PROXY_URL,
  HTTP2_PROXY_URL_REMOTE,
  PROXY_TYPE,
  PROXY_BATCH_SIZE,
  PROXY_BATCH_TIMEOUT_MS,
  PROXY_BATCH_BYTES_THRESHOLD,
  USE_OLD_RANGER } from
'./RcsConstants.js';
import { WebSocketBatcher } from './RcsWebSocketBatcher.js';
import { isLocalhostUrl } from './RcsUrlUtils.js';

/** @import {RcsFileLoader} from './RcsFileLoader' */

/** @type {typeof RcsFileLoader|null} */
let RcsFileLoaderRef = null;


/**
 * Register the RcsFileLoader class so that I/O operations can update
 * the global `totalReceivedBytes` counter.
 * @param {typeof RcsFileLoader} loader - The RcsFileLoader class (not an instance)
 */
export function setRcsFileLoaderReference(loader) {
  RcsFileLoaderRef = loader;
}

/**
 * Abstract base class for RCS file I/O.
 * Subclasses implement environment-specific byte-range reading.
 */
export class RcsIO {
  /**
   * @param {string} source - File path (Node.js) or URL (browser)
   */
  constructor(source) {
    /** @type {string} - File path or URL */
    this.source = source;
  }

  /**
   * Open the RCS file / resource for reading.
   * @returns {Promise<void>}
   */
  async open() {
    throw new Error('open() must be implemented by subclass');
  }

  /**
   * Close the RCS file / resource and release handles.
   * @returns {Promise<void>}
   */
  async close() {

    // Default no-op — subclasses may override.
  }
  /**
   * Read a byte range from the RCS file.
   * @param {number} offset - Byte offset to start reading from
   * @param {number} length - Number of bytes to read
   * @returns {Promise<Uint8Array>} The requested bytes
   */
  async readRange(offset, length) {
    throw new Error('readRange() must be implemented by subclass');
  }
}

/**
 * Node.js implementation using `fs.promises` file handles.
 */
export class NodeRcsIO extends RcsIO {
  /**
   * @param {string} filePath - Absolute or relative file system path
   */
  constructor(filePath) {
    super(filePath);
    this.fileHandle = null;
    this.fs = null;
  }

  /** @returns {Promise<void>} */
  async open() {
    // Dynamic import to avoid issues in browser environment
    // @ts-ignore Ignore node Implementation
    this.fs = await import('fs');

    if (!this.fs.existsSync(this.source)) {
      throw new Error(`File not found: ${this.source}`);
    }

    this.fileHandle = await this.fs.promises.open(this.source, 'r');
  }

  /** @returns {Promise<void>} */
  async close() {
    if (this.fileHandle) {
      await this.fileHandle.close();
      this.fileHandle = null;
    }
  }

  /**
   * @param {number} offset
   * @param {number} length
   * @returns {Promise<Uint8Array>}
   */
  async readRange(offset, length) {
    if (!this.fileHandle) {
      throw new Error('File not open');
    }

    const buffer = new Uint8Array(length);
    const { bytesRead } = await this.fileHandle.read(buffer, 0, length, offset);

    if (bytesRead < length) {
      throw new Error(`Expected to read ${length} bytes, got ${bytesRead}`);
    }

    // Track received bytes
    if (RcsFileLoaderRef) {
      RcsFileLoaderRef.totalReceivedBytes += bytesRead;
    }

    return buffer;
  }
}

/**
 * Browser implementation using XMLHttpRequest with HTTP Range headers.
 */
export class BrowserRcsIO extends RcsIO {
  /**
   * @param {string} url - HTTP(S) URL of the RCS file
   */
  constructor(url) {
    super(url);
  }

  /** @returns {Promise<void>} */
  async open() {

    // Nothing to initialise; range requests are stateless.
  }
  /** @returns {Promise<void>} */
  async close() {

    // No cleanup needed for browser implementation.
  }
  /**
   * @param {number} offset - Byte offset in the file to start reading from
   * @param {number} length - Number of bytes to read
   * @returns {Promise<Uint8Array>}
   */
  async readRange(offset, length) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', this.source, true);
      xhr.responseType = 'arraybuffer';

      // Set Range header for partial content
      xhr.setRequestHeader('Range', `bytes=${offset}-${offset + length - 1}`);

      xhr.onload = function () {
        if (xhr.status === 206) {
          const arrayBuffer = xhr.response;
          if (arrayBuffer.byteLength !== length) {
            reject(new Error(`Expected to read ${length} bytes, got ${arrayBuffer.byteLength}`));
            return;
          }

          // Track received bytes
          if (RcsFileLoaderRef) {
            RcsFileLoaderRef.totalReceivedBytes += arrayBuffer.byteLength;
          }

          resolve(new Uint8Array(arrayBuffer));
        } else if (xhr.status === 200) {
          // Server ignored the Range header and returned the full resource.
          reject(new Error(
            `Server ignored Range header (returned 200 OK for bytes=${offset}-${offset + length - 1}); ` +
            `byte-range requests are required.`
          ));
        } else if (xhr.status === 416) {
          reject(new Error(
            `Range not satisfiable: bytes=${offset}-${offset + length - 1} (read past end of file?)`
          ));
        } else {
          reject(new Error(`HTTP error: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = function () {
        reject(new Error('Network error during range request'));
      };

      xhr.send();
    });
  }
}

/**
 * Browser implementation that routes byte-range reads through the persistent
 * WebSocket proxy used by RcsDecodeWorker, so the main-thread bootstrap
 * (header / chunk directory / umbrella octree) goes through the same channel
 * as the worker's bulk geometry fetches.
 */
export class WebSocketRcsIO extends RcsIO {
  /**
   * @param {string|null} url - HTTP(S) URL of the RCS file; null in new remote ranger mode where
   *                            the resource is identified by URN rather than URL
   * @param {string} urn - URN identifying the model resource
   * @param {string} token - Bearer token for authentication
   */
  constructor(url, urn, token) {
    super(url);
    if (!urn || !token) {
      throw new Error('WebSocketRcsIO requires both urn and token for WebSocket authentication');
    }
    this.urn = urn;
    this.token = token;
    this.batcher = getSharedWebSocketBatcher();
  }

  async open() {

    // WebSocket is opened lazily by the batcher on the first readRange() call
  }
  async close() {

    // The shared batcher manages its own connection lifecycle
  }
  /**
   * @param {number} offset - Byte offset in the file to start reading from
   * @param {number} length - Number of bytes to read
   * @returns {Promise<Uint8Array>}
   */
  async readRange(offset, length) {
    const data = await this.batcher.fetchRange(this.source, offset, length, this.urn, this.token);

    if (RcsFileLoaderRef) {
      RcsFileLoaderRef.totalReceivedBytes += data.byteLength;
    }

    return data;
  }
}

/** @type {WebSocketBatcher|null} */
let sharedWebSocketBatcher = null;
function getSharedWebSocketBatcher() {
  if (!sharedWebSocketBatcher) {
    const useRemote = PROXY_TYPE === 'ranger_ws_remote' || PROXY_TYPE === 'ranger_ws_remote_old';
    const proxyUrl = useRemote ? HTTP2_PROXY_URL_REMOTE : HTTP2_PROXY_URL;
    sharedWebSocketBatcher = new WebSocketBatcher(
      proxyUrl,
      PROXY_BATCH_SIZE,
      PROXY_BATCH_TIMEOUT_MS,
      PROXY_BATCH_BYTES_THRESHOLD,
      useRemote
    );
    console.log(`RcsIO: Using WebSocket proxy at ${proxyUrl}`);
  }
  return sharedWebSocketBatcher;
}

/**
 * Factory function to create appropriate RcsIO instance based on environment
 * and proxy configuration.
 *
 * In the browser:
 * - New ranger mode (`!USE_OLD_RANGER` + URN present): always uses WebSocketRcsIO
 *   with a null source URL because the resource is identified by URN in the auth
 *   messages, not by URL. Works for both local and remote proxies.
 * - Old ranger mode: uses WebSocketRcsIO for non-localhost URLs, otherwise
 *   falls back to BrowserRcsIO (direct XHR with Range headers).
 *
 * @param {string|null} source - File path (Node.js) or URL (browser); null is
 *                               accepted in new remote ranger mode
 * @param {string} [urn]   - URN of the model being loaded; required in new
 *                           remote ranger mode for WebSocket authentication.
 * @param {string} [token] - Bearer token for the WebSocket auth header.
 * @returns {RcsIO} Appropriate RcsIO instance
 */
export function createRcsIO(source, urn, token) {
  // Detect environment
  if (typeof window !== 'undefined' && typeof XMLHttpRequest !== 'undefined') {
    // Browser environment
    const wsProxyConfigured = HTTP2_PROXY_URL && (
    PROXY_TYPE === 'ranger_ws' || PROXY_TYPE === 'ranger_ws_remote' || PROXY_TYPE === 'ranger_ws_remote_old');
    if (wsProxyConfigured) {
      if (urn && !USE_OLD_RANGER) {
        // New ranger (local or remote): resource is identified by URN via auth
        // messages, not by URL. No resource URL is needed so we pass null as source.
        return new WebSocketRcsIO(null, urn, token);
      }
      if (!source || !isLocalhostUrl(source)) {
        // Old ranger: route non-localhost URLs through the WebSocket proxy
        return new WebSocketRcsIO(source, urn, token);
      }
    }
    return new BrowserRcsIO(source);
    // @ts-ignore `process` is a Node.js global; unknown in browser type libs.
  } else if (typeof process !== 'undefined' && process.versions && process.versions.node) {
    return new NodeRcsIO(source);
  } else {
    throw new Error('Unsupported environment: neither browser nor Node.js detected');
  }
}